import { getApiUrl } from '../../utils/api';

/**
 * Hidden landing page for long-inactive customers. The path is random on
 * purpose (never linked, noindex) and must match SHOWCASE_PATH in the backend
 * (feature-showcase.service.ts), which builds the e-mail links.
 */
export const SHOWCASE_PATH = '/neu-entdecken-q7k2v9xm4t';

const TOKEN_KEY = 'herzens-showcase-token';
const TOKEN_RE = /^[a-f0-9]{32}$/;

/** Per-recipient token from the e-mail link; kept for the tab so in-page navigation keeps it. */
export const readShowcaseToken = (search: string): string | null => {
  const fromUrl = new URLSearchParams(search).get('t');
  try {
    if (fromUrl && TOKEN_RE.test(fromUrl)) {
      sessionStorage.setItem(TOKEN_KEY, fromUrl);
      return fromUrl;
    }
    const stored = sessionStorage.getItem(TOKEN_KEY);
    return stored && TOKEN_RE.test(stored) ? stored : null;
  } catch {
    return fromUrl && TOKEN_RE.test(fromUrl) ? fromUrl : null;
  }
};

/**
 * Plain fetch, not apiClient: demo mode swallows every apiClient write while a
 * preview runs, and this must still reach the server. keepalive lets the last
 * ping survive closing the tab.
 */
export const trackShowcase = (
  token: string | null,
  event: { type: 'visit' | 'feature' | 'ping'; feature?: string; seconds?: number },
) => {
  if (!token) return;
  try {
    void fetch(`${getApiUrl()}/feature-showcase/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ t: token, ...event }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* tracking must never break the page */
  }
};

const PING_EVERY_MS = 15000;

/**
 * Counts the seconds the page is visible and reports them every 15 s (and
 * when the tab is hidden or the view changes) under the given feature key.
 * Returns the cleanup function.
 */
export const startShowcaseTimer = (token: string | null, feature: string) => {
  if (!token) return () => undefined;
  let since = document.visibilityState === 'visible' ? Date.now() : null;
  const flush = () => {
    if (since === null) return;
    const seconds = Math.round((Date.now() - since) / 1000);
    since = document.visibilityState === 'visible' ? Date.now() : null;
    if (seconds > 0) trackShowcase(token, { type: 'ping', feature, seconds });
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      if (since === null) since = Date.now();
    } else {
      flush();
      since = null;
    }
  };
  const timer = window.setInterval(flush, PING_EVERY_MS);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flush);
  return () => {
    flush();
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', flush);
  };
};
